/**
 * FirmwareFlashingStep Component
 *
 * Step 5: Flash firmware to the device's storage
 * The device is autonomously flashing firmware - user waits for completion.
 */

import { Button } from '@ui/shared/Button';
import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import { Icon } from '../../../../shared/Icon';
import { useWizard } from '../../hooks/useWizard';
import { useUpdaterServices } from '../../hooks/useUpdaterServices';
import { HelpFooter } from '../HelpFooter';
import { WarningBanner } from '../WarningBanner';
import {
  StepState,
  StateActionType,
  WizardStep,
} from '../../types/wizard-types';

/**
 * FirmwareFlashingStep component
 *
 * The Easy Installer is running autonomously on the device and flashing
 * the firmware to internal storage. The user must wait for the green
 * light to stop flashing and the device to power down, then click Continue.
 */
export const FirmwareFlashingStep: React.FC = (): React.ReactElement => {
  const { dispatch, nextStep } = useWizard();
  const { logger } = useUpdaterServices();

  const [hasStarted, setHasStarted] = React.useState(false);

  // Mark step as in progress when component mounts
  React.useEffect((): void => {
    if (hasStarted) return;
    setHasStarted(true);

    dispatch({
      type: StateActionType.SET_STEP_STATE,
      step: WizardStep.FIRMWARE_FLASHING,
      state: StepState.IN_PROGRESS,
    });

    logger.info(
      'Firmware flashing step - waiting for device to complete installation'
    );
    logger.info('Power light should be flashing green on the device');
  }, [hasStarted, dispatch, logger]);

  /**
   * Handle continue button click
   */
  const handleContinue = (): void => {
    logger.success(
      'User confirmed device has powered down - firmware installation complete'
    );

    // Mark step as completed
    dispatch({
      type: StateActionType.SET_STEP_STATE,
      step: WizardStep.FIRMWARE_FLASHING,
      state: StepState.COMPLETED,
    });

    // Advance to next step
    nextStep();
  };

  return (
    <StepContainer>
      {/* Warning Banner */}
      <WarningBanner
        title='Do not disconnect your plates!'
        message='Keep your plates connected until this step completes.'
      />

      {/* Main Status Display */}
      <MainStatusSection>
        <StatusIndicator>
          <PulsingLight />
        </StatusIndicator>
        <StatusContent>
          <StatusTitle>Installing Firmware</StatusTitle>
          <StatusDescription>
            Your plate is installing the firmware update. Look for the{' '}
            <GreenText>flashing green power light</GreenText> on your plates.
          </StatusDescription>
        </StatusContent>
      </MainStatusSection>

      {/* Instructions */}
      <InstructionsSection>
        <InstructionTitle>
          <Icon name='hourglass_empty' size='small' />
          <span>What to expect</span>
        </InstructionTitle>
        <InstructionsList>
          <InstructionItem>
            <StepNumber>1</StepNumber>
            <InstructionText>
              The <GreenText>power light will flash green</GreenText> while the
              firmware is being installed. This typically takes{' '}
              <strong>2-3 minutes</strong>.
            </InstructionText>
          </InstructionItem>
          <InstructionItem>
            <StepNumber>2</StepNumber>
            <InstructionText>
              When installation is complete, the{' '}
              <strong>green light will stop flashing</strong> and the plates
              will <strong>power down automatically</strong>.
            </InstructionText>
          </InstructionItem>
          <InstructionItem>
            <StepNumber>3</StepNumber>
            <InstructionText>
              Once the plates have powered down, click the{' '}
              <strong>Continue</strong> button below.
            </InstructionText>
          </InstructionItem>
        </InstructionsList>
      </InstructionsSection>

      {/* Continue Button */}
      <ContinueSection>
        <ContinuePrompt>
          <Icon name='power_settings_new' size='small' />
          <span>Have your plates powered down?</span>
        </ContinuePrompt>
        <ContinueButton
          icon='check'
          title='Continue'
          flavor='primary'
          onClick={handleContinue}
        />
        <ContinueHint>
          Click Continue only after the green light stops flashing and the
          plates are powered off.
        </ContinueHint>
      </ContinueSection>

      {/* Contact Support */}
      <HelpFooter />
    </StepContainer>
  );
};

// Styled Components

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const MainStatusSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px;
  background: var(--color-surface-1);
  border-radius: 10px;
  border: 2px solid rgba(102, 187, 106, 0.3);
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  flex-shrink: 0;
`;

const pulseGreen = keyframes`
  0%, 100% {
    opacity: 0.4;
    transform: scale(0.85);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
`;

const PulsingLight = styled.div`
  width: 32px;
  height: 32px;
  background: #66bb6a;
  border-radius: 50%;
  box-shadow: 0 0 30px rgba(102, 187, 106, 0.7);
  animation: ${pulseGreen} 1s ease-in-out infinite;
`;

const StatusContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StatusTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text);
`;

const StatusDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.5;
`;

const GreenText = styled.span`
  color: #66bb6a;
  font-weight: 500;
`;

const InstructionsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  background: var(--color-surface-1);
  border-radius: 10px;
`;

const InstructionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text);
`;

const InstructionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InstructionItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const StepNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: var(--color-primary);
  border-radius: 50%;
  font-size: 13px;
  font-weight: 600;
  color: white;
  flex-shrink: 0;
`;

const InstructionText = styled.p`
  margin: 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.5;

  strong {
    color: var(--color-text);
  }
`;

const ContinueSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px;
  background: var(--color-surface-1);
  border-radius: 10px;
  border: 2px dashed var(--color-primary);
`;

const ContinuePrompt = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 500;
  color: var(--color-text);
`;

const ContinueButton = styled(Button)`
  min-width: 200px;
  justify-content: center;

  span {
    margin-left: 8px;
  }
`;

const ContinueHint = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
`;

export default FirmwareFlashingStep;
